import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Stack,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
  FormControl,
} from '@mui/material'
import { BootstrapDialog } from 'components/BootstrapDialog'
import { DialogCloseButton } from 'components/DialogCloseButton'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'
import { createReportSchema } from './validations'
import { IInfoCenterReport } from 'types/info-center/reports'
import { useCreateReportMutation } from 'services/info-center/reports'
import { FileUpload } from 'components/FileUpload'

export type ReportFieldValues = {
  title: string
  reportFileId?: string | undefined
}

type Props = {
  onClose: () => void
  report?: IInfoCenterReport
}

export const DialogCreateReport = ({ onClose, report }: Props) => {
  const { t } = useTranslation()
  const schema = createReportSchema(t)

  const [createReport, { isSuccess, isError }] = useCreateReportMutation()

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm<ReportFieldValues>({
    defaultValues: {
      title: report?.title ?? '',
      reportFileId: report?.reportFileId ?? '',
    },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (isSuccess) {
      onClose()
      toast.success(t('operation-success'))
    }
    if (isError) {
      toast.error(t('failedToCreate'))
    }
  }, [isSuccess, isError, t, onClose])

  const onSubmit = ({ title, reportFileId }: ReportFieldValues) => {
    if (report) {
      createReport({
        id: report.id,
        title,
        reportFileId,
      })
    } else {
      createReport({
        id: uuidv4(),
        title,
        reportFileId,
      })
    }
  }

  return (
    <BootstrapDialog open maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, fontWeight: 'bold' }}>
        {t('reports')}
      </DialogTitle>

      <DialogCloseButton handleClose={onClose} />

      <DialogContent dividers>
        <Stack spacing={2}>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t('title')}
                  size="small"
                  variant="outlined"
                />
                {errors.title && (
                  <FormHelperText error>{errors.title?.message}</FormHelperText>
                )}
              </Box>
            )}
          />
          <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
            <FileUpload
              text={t('creationOrderFile')}
              handleFile={(file) => {
                setValue('reportFileId', file.id)
              }}
              onDelete={() => setValue('reportFileId', undefined)}
              fileId={report?.reportFileId ?? undefined}
            />
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack width="100%" direction="row" justifyContent="space-between">
          <Button onClick={onClose} variant="outlined">
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            autoFocus
            onClick={handleSubmit(onSubmit)}
          >
            {t('create')}
          </Button>
        </Stack>
      </DialogActions>
    </BootstrapDialog>
  )
}
